import { useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import { AdaptiveSheet } from '@shared/ui/AdaptiveSheet'
import { Button } from '@shared/ui/Button'
import type { SubscriptionOverride, SubscriptionPrice } from '@/types/api'
import { applyOverride } from '../../model/applyOverride'
import { formatPlanName, formatPrice } from '../../model/usePlanLookup'
import styles from './ChangePlanModal.module.css'

interface ChangePlanModalProps {
  /** Справочник тарифов из `useSubscriptionPricesQuery`. */
  plans: SubscriptionPrice[]
  override?: SubscriptionOverride | null
  /** Текущий тариф подписки — его в списке не показываем. */
  currentPlanId?: number
  /** Уже выбранный отложенный тариф, если смена назначена ранее. */
  nextPlanId?: number | null
  /** Дата ближайшего списания в формате дд.мм.гггг — для текста подтверждения. */
  nextChargeDate?: string | null
  isSubmitting?: boolean
  errorText?: string | null
  /** `null` — отменить назначенную смену и остаться на текущем тарифе. */
  onSubmit: (planId: number | null) => void
  onClose: () => void
}

/**
 * Выбор тарифа, на который подписка перейдёт при следующем автосписании.
 *
 * Текущий тариф в списке не показываем: переключаться на него незачем, а на
 * бэке он к тому же означает отмену смены — кнопка «Оставить текущий тариф»
 * делает это явно и появляется, только когда отменять есть что.
 *
 * Деньги здесь не двигаются: оплаченный период доживает до конца, новая цена
 * спишется в момент автоплатежа. Поэтому и текст говорит про следующее
 * списание, а не про оплату сейчас.
 */
export const ChangePlanModal = ({
  plans,
  override,
  currentPlanId,
  nextPlanId,
  nextChargeDate,
  isSubmitting = false,
  errorText,
  onSubmit,
  onClose,
}: ChangePlanModalProps) => {
  const options = useMemo(
    () =>
      plans
        .filter(
          plan =>
            plan.is_active !== false &&
            plan.subscription_info_id !== currentPlanId
        )
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [plans, currentPlanId]
  )

  const [selectedId, setSelectedId] = useState<number | null>(
    nextPlanId ?? null
  )

  const selected = options.find(p => p.subscription_info_id === selectedId)
  // Кнопка ждёт осмысленного выбора: тот же тариф подтверждать нечего.
  const canSubmit = selectedId != null && selectedId !== nextPlanId
  const hasPendingChange = nextPlanId != null && nextPlanId !== currentPlanId

  return (
    <AdaptiveSheet onClose={onClose}>
      <div className={styles.content}>
        <h3 className={styles.title}>Сменить тариф</h3>
        <p className={styles.text}>
          Текущий тариф действует до конца оплаченного периода. Новый применится
          при следующем списании
          {nextChargeDate ? ` ${nextChargeDate}` : ''} — тогда же спишется его
          стоимость.
        </p>

        {options.length === 0 ? (
          <p className={styles.text}>Других тарифов сейчас нет.</p>
        ) : (
          <div className={styles.optionList}>
            {options.map(plan => {
              const isSelected = plan.subscription_info_id === selectedId
              const price = applyOverride(plan, override)
              return (
                <button
                  key={plan.subscription_info_id}
                  type="button"
                  className={`${styles.planRow} ${isSelected ? styles.planRowActive : ''}`}
                  onClick={() => setSelectedId(plan.subscription_info_id)}
                  disabled={isSubmitting}
                >
                  <span className={styles.planLeft}>
                    <span
                      className={`${styles.checkSquare} ${isSelected ? styles.checkSquareActive : ''}`}
                      aria-hidden
                    >
                      {isSelected && <Check size={14} strokeWidth={3} />}
                    </span>
                    <span className={styles.planName}>
                      {formatPlanName(plan)}
                    </span>
                  </span>
                  <span className={styles.planPrice}>
                    {price.hasDiscount && (
                      <span className={styles.planPriceOld}>
                        {formatPrice(price.basePrice, price.baseCurrency)}
                      </span>
                    )}
                    {formatPrice(price.finalPrice, price.finalCurrency)}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {errorText && <p className={styles.error}>{errorText}</p>}

        <Button
          onClick={() => onSubmit(selectedId)}
          disabled={!canSubmit || isSubmitting}
          block
        >
          {isSubmitting
            ? 'Сохраняем…'
            : selectedId != null && selectedId === nextPlanId
              ? 'Этот тариф уже выбран'
              : selected
                ? `Перейти на ${formatPlanName(selected).toLowerCase()}`
                : 'Выберите тариф'}
        </Button>

        {hasPendingChange && (
          <Button
            variant="secondary"
            onClick={() => onSubmit(null)}
            disabled={isSubmitting}
            block
          >
            Оставить текущий тариф
          </Button>
        )}
      </div>
    </AdaptiveSheet>
  )
}
